import React from 'react';
import ServiceTemplate from '@/components/ServiceTemplate';

const PvtLtdCompliance = () => {
  const data = {
    title: 'Private Limited Company Compliance',
    description: 'Complete annual compliance services for Private Limited Companies. Stay compliant with MCA, Income Tax, and all statutory requirements with expert CA guidance.',
    keywords: 'company compliance, annual compliance, ROC filing, MCA compliance, AOC-4, MGT-7, company annual return, statutory compliance',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Private Limited Companies in India are required to maintain strict compliance with the Companies Act 2013, 
          Income Tax Act, and various other regulations. Non-compliance can result in heavy penalties, director 
          disqualification, and even company strike-off. Our comprehensive compliance package ensures your company 
          stays compliant throughout the year.
        </p>
        <p>
          <strong>Why is Compliance Important?</strong> The Ministry of Corporate Affairs (MCA) actively monitors 
          company filings. Late or non-filing of annual returns attracts penalties of ₹100 per day, which can 
          accumulate to lakhs. Directors of non-compliant companies can be disqualified from holding directorship 
          in any company. Bank loans, tenders, and business contracts often require compliance certificates.
        </p>
        <p>
          <strong>Our Compliance Approach:</strong> We assign a dedicated Chartered Accountant to your company who 
          understands your business and ensures timely filing of all returns. Our team sends proactive reminders, 
          prepares documents, and handles all MCA and Income Tax filings. We also provide ongoing consultation for 
          day-to-day compliance queries.
        </p>
        <p>
          <strong>Compliance Calendar:</strong> Company compliance follows a strict calendar – Board meetings every 
          quarter, AGM within 6 months of financial year end, ROC filings within 30/60 days of AGM, and Income Tax 
          return by 31st October. Missing any deadline triggers penalties and legal complications.
        </p>
      </div>
    ),
    
    benefits: [
      'Avoid heavy penalties – late filing penalty is ₹100/day (can reach lakhs)',
      'Prevent director disqualification – protects your directorship in all companies',
      'Maintain good standing for bank loans and business contracts',
      'Expert CA guidance for all compliance matters',
      'Timely reminders and proactive compliance management',
      'Board meeting support with agenda and minutes drafting',
      'AGM planning and documentation assistance',
      'Financial statement preparation as per Schedule III',
      'Statutory audit coordination with qualified auditors',
      'Income Tax planning and return filing',
      'GST compliance support (optional add-on)',
      'Single point of contact for all compliance queries',
    ],
    
    documents: [
      'Previous year audited financial statements',
      'Bank statements for the entire financial year',
      'All sales and purchase invoices',
      'Expense bills and vouchers with proper documentation',
      'Salary records and TDS challans',
      'Tax payment challans (advance tax, self-assessment)',
      'Investment proofs and capital additions',
      'Loan statements (if any)',
      'Director DSC (Digital Signature Certificates)',
      'Minutes of previous board meetings and AGM',
      'Statutory registers (if maintained physically)',
      'Previous ROC filing acknowledgments',
    ],
    
    process: [
      'Financial statements preparation as per Companies Act and Accounting Standards',
      'Coordination with statutory auditor for audit completion',
      'Board meeting for adoption of financial statements',
      'Annual General Meeting (AGM) convening and documentation',
      'Preparation and filing of Form AOC-4 (financial statements) with ROC',
      'Preparation and filing of Form MGT-7 (annual return) with ROC',
      'Income Tax Return (ITR-6) preparation and filing',
      'TDS return reconciliation and filing (if applicable)',
      'Advance tax calculation and payment assistance',
      'Maintenance of statutory registers and minute books',
    ],
    
    timeline: 'Ongoing Annual Process',
    pricing: '₹15,999/year',
    
    additionalInfo: (
      <div className="space-y-3">
        <p><strong>Important Deadlines:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Board Meetings: Minimum 4 per year (gap not exceeding 120 days)</li>
          <li>First Board Meeting: Within 30 days of incorporation</li>
          <li>AGM: Within 6 months from end of financial year (30th September)</li>
          <li>Form AOC-4: Within 30 days of AGM</li>
          <li>Form MGT-7: Within 60 days of AGM</li>
          <li>Income Tax Return: 31st October (for companies)</li>
          <li>Advance Tax: 15th June, Sept, Dec, March</li>
        </ul>
        <p className="mt-3"><strong>Penalty Structure:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Late AOC-4/MGT-7 filing: ₹100 per day (no maximum cap)</li>
          <li>Non-holding of AGM: ₹1 lakh for company + ₹5,000 per defaulting officer</li>
          <li>Late Income Tax Return: ₹5,000 to ₹10,000 + interest on tax</li>
          <li>Director disqualification after continuous default of 3 years</li>
        </ul>
        <p className="mt-3"><strong>Package Includes:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Form AOC-4 and MGT-7 filing</li>
          <li>Financial statements preparation</li>
          <li>Board meeting minutes drafting (4 meetings)</li>
          <li>AGM notice and minutes</li>
          <li>Income Tax Return filing (ITR-6)</li>
          <li>Audit coordination (auditor fees extra)</li>
          <li>Year-round compliance consultation</li>
        </ul>
      </div>
    ),
    
    faqs: [
      {
        question: 'What are annual compliance requirements for Private Limited Company?',
        answer: 'Private Limited Companies must file annual returns (MGT-7), financial statements (AOC-4), hold minimum 4 board meetings, conduct AGM, file Income Tax returns, maintain statutory registers, and get accounts audited by a Chartered Accountant.',
      },
      {
        question: 'What happens if compliance is not done?',
        answer: 'Non-compliance attracts heavy penalties from MCA (₹100/day for late filing), can lead to director disqualification (DIN deactivation), company may be struck off from register, and directors face prosecution. Outstanding compliance must be cleared before any other filing.',
      },
      {
        question: 'Is statutory audit mandatory for all Private Limited Companies?',
        answer: 'Yes, every Private Limited Company must get its books audited by a practicing Chartered Accountant, regardless of turnover. The auditor must be appointed within 30 days of incorporation and at every AGM for the next year.',
      },
      {
        question: 'How many board meetings are required in a year?',
        answer: 'Minimum 4 board meetings per year with gap not exceeding 120 days between two consecutive meetings. At least one meeting per quarter. First board meeting within 30 days of incorporation. One-Person Company needs only 2 meetings.',
      },
      {
        question: 'What is Form AOC-4 and MGT-7?',
        answer: 'Form AOC-4 is for filing financial statements (Balance Sheet, P&L, Notes, Audit Report) with ROC – due within 30 days of AGM. Form MGT-7 is Annual Return with details of shareholders, directors, and meetings – due within 60 days of AGM.',
      },
      {
        question: 'Can dormant company avoid compliance?',
        answer: 'Dormant companies (not carrying any business) have reduced compliance but still must file annual returns, hold board meetings, and maintain registered office. They can file simplified returns after getting "Dormant Company" status from ROC.',
      },
      {
        question: 'What statutory registers must a company maintain?',
        answer: 'Companies must maintain: Register of Members, Register of Directors, Register of Charges, Register of Loans/Investments, Minutes Books for Board and General Meetings. These must be kept at registered office and produced during inspection.',
      },
      {
        question: 'How to clear pending compliance of previous years?',
        answer: 'Pending compliance can be cleared by filing belated returns with applicable late fees. If directors are disqualified, they must first file pending returns, wait for DIN restoration, then proceed with current filings. We can help regularize all pending compliance.',
      },
    ],
  };

  return <ServiceTemplate {...data} />;
};

export default PvtLtdCompliance;
